export type UserLevel = 'FREE' | 'STANDARD' | 'PROFESSIONAL'

export interface User {
  userId: string
  name: string
  email: string
  userLevel: UserLevel
  // 백엔드 Jackson이 boolean isAdmin 필드를 "is" 접두사를 제거한 "admin" 키로 직렬화함
  // (MeshData.isSample → sample과 동일한 기존 관례를 따름)
  admin: boolean
}

export interface AuthState {
  token: string | null
  user: User | null
  expiresAt: number | null
}

export type ConversionStatus = 'PENDING' | 'CONVERTING' | 'SUCCESS' | 'FAILED'

export interface MeshData {
  id: number
  name: string
  description: string | null
  originalFileName: string
  status: ConversionStatus
  tileUrl: string | null
  sample: boolean
  originalFileSize: number | null
  hasOriginalFile: boolean
  createdAt: string
  convertedAt: string | null
}

export interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface UserProfile {
  userId: string
  name: string
  email: string
  organization: string | null
  loginType: 'LOCAL' | 'NAVER'
  userLevel: UserLevel
  createdAt: string
  admin: boolean
}

export interface PeriodStat {
  year: number
  period: number
  count: number
}

export interface DataItemStat {
  id: number
  tileSize: number
}

export interface UserStats {
  totalCount: number
  successCount: number
  failedCount: number
  convertingCount: number
  pendingCount: number
  totalOriginalSize: number
  storageSize: number
  tileNetworkUsage: number
  dailyStats: PeriodStat[]
  monthlyStats: PeriodStat[]
  weeklyStats: PeriodStat[]
  dataSizes: DataItemStat[]
}

export interface DailyStatPoint {
  date: string
  bytes: number
}

export interface StreamingStats {
  totalBytes: number
  dailyStats: DailyStatPoint[]
}

export interface QuotaItem {
  resourceType: string
  usedBytes: number
  limitBytes: number
  usedGib: number
  limitGib: number
  percentage: number
  exceeded: boolean
}

export interface UsageQuota {
  billingCycle: string
  conversion: QuotaItem
  storage: QuotaItem
  streaming: QuotaItem
  downloadPolicy: string
}

export interface BillingSummary {
  userLevel: UserLevel
  levelLabel: string
  monthlyFee: number
  billingCycle: string
  nextBillingDate: string
  hasPaymentMethod: boolean
  scheduledLevel: UserLevel | null
  scheduledLevelLabel: string | null
}

export interface Invoice {
  billingCycle: string
  userLevel: UserLevel
  levelLabel: string
  basicFee: number
  status: 'FREE' | 'PENDING' | 'PAID'
}

export type InquiryCategory = 'TECH_SUPPORT' | 'BILLING' | 'PARTNERSHIP' | 'OTHER'

export const INQUIRY_CATEGORY_OPTIONS: { value: InquiryCategory; label: string }[] = [
  { value: 'TECH_SUPPORT', label: '기술지원' },
  { value: 'BILLING', label: '결제/환불' },
  { value: 'PARTNERSHIP', label: '제휴 문의' },
  { value: 'OTHER', label: '기타' },
]

export interface ContactMessage {
  id: number
  title: string
  content?: string
  category: InquiryCategory
  categoryLabel: string
  contactEmail?: string
  authorName?: string
  authorEmail?: string
  hasAttachment: boolean
  attachmentFileName?: string
  createdAt: string
}

export type QnaCategory = 'SERVICE' | 'BILLING' | 'TECH_SUPPORT' | 'ACCOUNT'

export const QNA_CATEGORY_OPTIONS: { value: QnaCategory; label: string }[] = [
  { value: 'SERVICE', label: '서비스 이용' },
  { value: 'BILLING', label: '요금·결제' },
  { value: 'TECH_SUPPORT', label: '기술 지원' },
  { value: 'ACCOUNT', label: '계정' },
]

export interface QnaEntry {
  id: number
  question: string
  answer: string
  category: QnaCategory
  categoryLabel: string
  createdAt: string
  updatedAt: string | null
}
